import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Image as ImageIcon, Plus, Trash2, Upload } from 'lucide-react';
import { uploadService } from '../../api/uploadService';
import './PageContent.css';

/** Icon names the storefront knows how to render (see lib/content/contentIcons.ts). */
export const ICON_OPTIONS = [
  'activity', 'award', 'cart', 'check', 'clock', 'eye', 'fuel', 'handshake',
  'headphones', 'heart', 'mail', 'map-pin', 'navigation', 'package', 'phone',
  'route', 'satellite', 'shield', 'star', 'target', 'tool', 'trending-up',
  'truck', 'users', 'wifi', 'zap',
];

export const VALUE_COLORS = ['blue', 'red', 'green', 'purple', 'amber', 'teal'];
export const B2B_GRADIENTS = ['blue', 'emerald', 'amber', 'violet', 'rose'];

/** A titled block inside a page editor. */
export function Section({ title, description, children, right }) {
  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>{title}</h2>
          {description && <p className="pc-hint">{description}</p>}
        </div>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, hint, type = 'text' }) {
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}

export function NumberField({ label, value, onChange, hint, min, max }) {
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <input
        type="number"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}

export function TextAreaField({ label, value, onChange, rows = 3, placeholder, hint }) {
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <textarea
        rows={rows}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', resize: 'vertical' }}
      />
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}

export function SelectField({ label, value, onChange, options, hint }) {
  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? opt : opt.label;
          return (
            <option key={val} value={val}>
              {text}
            </option>
          );
        })}
      </select>
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}

export function CheckboxField({ label, checked, onChange, hint }) {
  return (
    <div className="form-group">
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 'auto', margin: 0 }}
        />
        {label}
      </label>
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}

/**
 * Image picker: paste a URL, or upload a file that goes to /storage/pages/images
 * and comes back as an absolute URL the storefront can render.
 */
export function ImageField({ label, value, onChange, hint, onError }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked after an error
    if (!file) return;

    setUploading(true);
    try {
      const url = await uploadService.uploadImage(file, 'pages');
      onChange(url);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Échec de l'envoi de l'image";
      if (onError) onError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div className="pc-image-field">
        {value ? (
          <img src={value} alt="" className="pc-image-preview" />
        ) : (
          <div className="pc-image-placeholder">
            <ImageIcon size={22} />
          </div>
        )}
        <div className="pc-image-controls">
          <input
            type="text"
            value={value ?? ''}
            placeholder="/images/... ou https://..."
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="pc-image-actions">
            <label className="pc-upload-label">
              <Upload size={14} />
              {uploading ? 'Envoi...' : 'Téléverser'}
              <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} />
            </label>
            {value && (
              <button type="button" className="pc-icon-btn pc-danger" onClick={() => onChange('')} title="Retirer l'image">
                <Trash2 size={15} />
              </button>
            )}
          </div>
          {hint && <p className="pc-hint">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

/** Reorder/remove/add controls shared by the repeatable editors. */
function ItemControls({ index, count, onMove, onRemove }) {
  return (
    <div className="pc-item-actions">
      <button
        type="button"
        className="pc-icon-btn"
        onClick={() => onMove(index, index - 1)}
        disabled={index === 0}
        title="Monter"
      >
        <ArrowUp size={15} />
      </button>
      <button
        type="button"
        className="pc-icon-btn"
        onClick={() => onMove(index, index + 1)}
        disabled={index === count - 1}
        title="Descendre"
      >
        <ArrowDown size={15} />
      </button>
      <button
        type="button"
        className="pc-icon-btn pc-danger"
        onClick={() => onRemove(index)}
        title="Supprimer"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

/**
 * Editor for a list of objects. `renderItem(item, update)` renders the fields;
 * `update(patch)` merges a partial into that item.
 */
export function RepeatableList({ items, onChange, renderItem, itemLabel, newItem, addLabel }) {
  const list = Array.isArray(items) ? items : [];

  const updateAt = (index, patch) => {
    onChange(list.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const move = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (index) => onChange(list.filter((_, i) => i !== index));

  return (
    <>
      {list.map((item, index) => (
        <div key={index} className="pc-item">
          <div className="pc-item-header">
            <span className="pc-item-title">
              {itemLabel ? `${itemLabel} ${index + 1}` : `Élément ${index + 1}`}
            </span>
            <ItemControls index={index} count={list.length} onMove={move} onRemove={remove} />
          </div>
          {renderItem(item, (patch) => updateAt(index, patch), index)}
        </div>
      ))}

      <button type="button" className="pc-add-btn" onClick={() => onChange([...list, newItem()])}>
        <Plus size={15} />
        {addLabel || 'Ajouter'}
      </button>
    </>
  );
}

/** Editor for a list of plain strings (paragraphs, bullet points, tags…). */
export function StringList({ label, items, onChange, placeholder, addLabel, multiline = false, hint }) {
  const list = Array.isArray(items) ? items : [];

  const move = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      {list.map((value, index) => (
        <div key={index} className="pc-string-row">
          {multiline ? (
            <textarea
              rows={3}
              value={value ?? ''}
              placeholder={placeholder}
              onChange={(e) => onChange(list.map((v, i) => (i === index ? e.target.value : v)))}
              style={{ resize: 'vertical' }}
            />
          ) : (
            <input
              type="text"
              value={value ?? ''}
              placeholder={placeholder}
              onChange={(e) => onChange(list.map((v, i) => (i === index ? e.target.value : v)))}
            />
          )}
          <ItemControls
            index={index}
            count={list.length}
            onMove={move}
            onRemove={(i) => onChange(list.filter((_, idx) => idx !== i))}
          />
        </div>
      ))}
      <button type="button" className="pc-add-btn" onClick={() => onChange([...list, ''])}>
        <Plus size={15} />
        {addLabel || 'Ajouter une ligne'}
      </button>
      {hint && <p className="pc-hint">{hint}</p>}
    </div>
  );
}
